import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';

// ── Sentry Error Tracking ────────────────────────────────────────────
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
  });
}

// ── Global Error Reporter ────────────────────────────────────────────
// Catches unhandled JS errors and promise rejections, sends to backend.
// Deduplication: max 1 report per error per 5 minutes.
const _reportedErrors = new Map<string, number>();

function reportError(payload: Record<string, unknown>) {
  const key = `${payload.message}:${payload.source}`;
  const now = Date.now();
  if (now - (_reportedErrors.get(key) || 0) < 300_000) return;
  _reportedErrors.set(key, now);

  const body = { ...payload, pageUrl: location.href, userAgent: navigator.userAgent };
  fetch('/api/error-report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => {}); // fire-and-forget
}

window.onerror = (message, source, line, col, error) => {
  reportError({ type: 'js_error', message: String(message), source, line, col, stack: error?.stack?.substring(0, 500) });
};

window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  const msg = event.reason?.message || String(event.reason);
  reportError({ type: 'unhandled_rejection', message: msg, stack: event.reason?.stack?.substring(0, 500) });
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
